import { egp, money } from '@ContentManagement/EnrollmentInvoices';
import { Typography } from '@mui/material';
import React from 'react';
import styled from 'styled-components';
import { WalletStatementData } from '../Types/Analytics.types';
import DistributionDonuts from './DistributionDonuts';
import { DataCardsRow } from './SummaryCards';

/**
 * The wallet statement: Money In / Money Out on the ledger's clock, then the
 * balance band. Replaces the old Charged/Spent/Remaining tiles, which mixed
 * range-filtered flows with an all-time stock and could never reconcile.
 */
const WalletStatement: React.FC<{
	statement: WalletStatementData;
	rangeEndsNow: boolean;
}> = ({ statement, rangeEndsNow }) => {
	const showUnlogged = Math.abs(statement.balance.unlogged) > 0.009;
	const openingBalance = statement.balance.remaining - statement.net;

	const inLines: Array<[string, number, boolean?]> = [
		['Online recharges (gateway)', statement.in.online_recharges],
		['Recharge cards', statement.in.printed_cards],
		['Admin credits', statement.in.admin_credits],
		['Refunds returned', statement.in.refunds_returned],
		['Unclassified (legacy)', statement.in.unclassified, statement.in.unclassified > 0],
	];
	const outLines: Array<[string, number, boolean?]> = [
		['Admin/instructor enrollments', statement.out.admin_enrollments],
		// Hidden when zero in the period — user rule.
		['Registration fees', statement.out.registration_fees, statement.out.registration_fees > 0],
		['Admin deductions', statement.out.admin_deductions],
		['Unclassified (legacy)', statement.out.unclassified, statement.out.unclassified > 0],
	];
	// The ledger↔seat-value bridge: purchases split by what later happened to
	// the purchased enrollment. `active` is what the seat-value figures see.
	const split = statement.out.purchase_split;
	const splitLines: Array<[string, number, boolean?]> = split
		? [
				['of which: enrollments later refunded', split.refunded_enrollments, split.refunded_enrollments > 0],
				['of which: enrollments later removed without refund', split.removed_enrollments, split.removed_enrollments > 0],
				['of which: earlier era (no financial link)', split.unlinked, split.unlinked > 0],
				['= spending on currently-active enrollments', split.active],
		  ]
		: [];
	const visible = (lines: Array<[string, number, boolean?]>) =>
		lines.filter(([, , show]) => show === undefined || show);

	const donutSlices = (rows: Array<{ name: string; value: number }>) =>
		rows
			.filter((row) => row.value > 0)
			.map((row, index) => ({ id: index + 1, name: row.name, value: row.value }));
	const providerSlices = donutSlices(statement.online_by_provider);
	const methodSlices = donutSlices(statement.online_by_method);

	return (
		<>
			<SectionTitle>Wallets</SectionTitle>
			<DataCardsRow
				items={[
					{ label: 'Total In', value: egp(statement.total_in), color: 'secondary' },
					{ label: 'Total Out', value: egp(statement.total_out), color: 'secondary' },
					{ label: 'Net Movement', value: egp(statement.net), sub: 'in the selected period', color: 'secondary' },
					{
						label: 'Balance Now',
						value: egp(statement.balance.remaining),
						sub: `all time — ${money(statement.balance.refundable)} refundable / ${money(
							statement.balance.nonrefundable
						)} non-refundable`,
						color: 'secondary',
					},
				]}
			/>

			<StatementGrid>
				<StatementColumn>
					<ColumnTitle>Money In</ColumnTitle>
					{visible(inLines).map(([label, value]) => (
						<Line key={label}>
							<span>{label}</span>
							<Amount>{egp(value)}</Amount>
						</Line>
					))}
					<TotalLine>
						<span>Total In</span>
						<Amount>{egp(statement.total_in)}</Amount>
					</TotalLine>
				</StatementColumn>
				<StatementColumn>
					<ColumnTitle>Money Out</ColumnTitle>
					<Line>
						<span>Course purchases (students)</span>
						<Amount>{egp(statement.out.student_purchases)}</Amount>
					</Line>
					{visible(splitLines).map(([label, value]) => (
						<SubLine key={label}>
							<span>{label}</span>
							<Amount>{egp(value)}</Amount>
						</SubLine>
					))}
					{visible(outLines).map(([label, value]) => (
						<Line key={label}>
							<span>{label}</span>
							<Amount>{egp(value)}</Amount>
						</Line>
					))}
					<TotalLine>
						<span>Total Out</span>
						<Amount>{egp(statement.total_out)}</Amount>
					</TotalLine>
				</StatementColumn>
			</StatementGrid>

			{rangeEndsNow && (
				<Footnote>
					Opening balance at the start of the period: {egp(openingBalance)} (balance now −
					net movement).
				</Footnote>
			)}
			{showUnlogged && (
				<WarningNote>
					Unlogged balance: {egp(statement.balance.unlogged)} — balances written into
					wallets with no ledger entry (seed/import). The statement lines cannot explain
					this amount.
				</WarningNote>
			)}
			<Footnote>
				Scratchcard enrollments never touch wallets, so Money Out is smaller than the
				seat-value figures by design.
			</Footnote>
			<Footnote>
				The statement is dated by wallet movement; the cards above value
				currently-active enrollments — the "currently-active" line is where the two
				views meet.
			</Footnote>

			{(providerSlices.length > 0 || methodSlices.length > 0) && (
				<>
					<DistributionDonuts
						enrollments={providerSlices}
						revenue={methodSlices}
						enrollmentsTitle="Online recharges by provider"
						revenueTitle="Online recharges by payment method"
						enrollmentsIsMoney
					/>
					<Footnote>
						Provider and payment-method shares come from paid recharge invoices and are
						dated by initiation; the statement lines above are dated by wallet credit.
					</Footnote>
				</>
			)}
		</>
	);
};

const SectionTitle = styled(Typography).attrs({ variant: 'h6' })`
	margin-bottom: 1rem;
`;

const StatementGrid = styled.div`
	display: grid;
	grid-template-columns: repeat(auto-fit, minmax(20rem, 1fr));
	gap: 1rem;
	width: 100%;
	margin-bottom: 1rem;
`;

const StatementColumn = styled.div`
	background: #fff;
	border-radius: 5px;
	padding: 1rem 1.25rem;
`;

const ColumnTitle = styled.div`
	font-weight: 600;
	font-size: 1rem;
	margin-bottom: 0.75rem;
`;

const Line = styled.div`
	display: flex;
	justify-content: space-between;
	gap: 1rem;
	padding: 0.35rem 0;
	font-size: 0.9rem;
	color: rgba(0, 0, 0, 0.75);
`;

const TotalLine = styled(Line)`
	border-top: 1px solid rgba(0, 0, 0, 0.12);
	margin-top: 0.5rem;
	padding-top: 0.6rem;
	font-weight: 700;
	color: rgba(0, 0, 0, 0.9);
`;

const SubLine = styled(Line)`
	padding: 0.15rem 0 0.15rem 1.25rem;
	font-size: 0.8rem;
	color: rgba(0, 0, 0, 0.55);
`;

const Amount = styled.span`
	font-weight: 600;
	white-space: nowrap;
`;

const Footnote = styled.div`
	font-size: 0.8rem;
	color: rgba(0, 0, 0, 0.55);
	margin: 0.25rem 0 1rem;
`;

const WarningNote = styled(Footnote)`
	color: #b71c1c;
	font-weight: 600;
`;

export default WalletStatement;
